import * as React from 'react';
import styled from 'styled-components';

import { createUIContext } from '../../internals/createContext';
import { composeRefs } from '../../internals/composeRefs';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

type Orientation = 'vertical' | 'horizontal';

interface ScrollAreaContextValue {
  viewportRef: React.RefObject<HTMLDivElement | null>;
  scrollHideDelay: number;
}

const [ScrollAreaProvider, useScrollAreaContext] = createUIContext<ScrollAreaContextValue>('ScrollArea');

interface ScrollbarLocalContextValue {
  orientation: Orientation;
  trackRef: React.RefObject<HTMLDivElement | null>;
}

const ScrollbarLocalContext = React.createContext<ScrollbarLocalContextValue | null>(null);

const StyledRoot = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export interface ScrollAreaRootProps extends React.HTMLAttributes<HTMLDivElement> {
  scrollHideDelay?: number;
  semanticId?: string;
}

const ScrollAreaRoot = React.forwardRef<HTMLDivElement, ScrollAreaRootProps>(function ScrollAreaRoot(
  { scrollHideDelay = 1000, semanticId, children, ...rest },
  ref,
) {
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const ctx = React.useMemo(() => ({ viewportRef, scrollHideDelay }), [scrollHideDelay]);
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <ScrollAreaProvider value={ctx}>
      <StyledRoot ref={ref} data-scrollarea-root="" {...semanticProps} {...rest}>
        {children}
      </StyledRoot>
    </ScrollAreaProvider>
  );
});

ScrollAreaRoot.displayName = 'ScrollArea.Root';

const StyledViewport = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export interface ScrollAreaViewportProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const ScrollAreaViewport = React.forwardRef<HTMLDivElement, ScrollAreaViewportProps>(
  function ScrollAreaViewport({ semanticId, onScroll, ...rest }, ref) {
    const { viewportRef, scrollHideDelay } = useScrollAreaContext();
    const composed = composeRefs(ref, viewportRef);
    const semanticProps = getSemanticAttributes(semanticId);
    const hideTimer = React.useRef<number>();

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      onScroll?.(e);
      const root = e.currentTarget.closest('[data-scrollarea-root]') as HTMLElement | null;
      const bars = root?.querySelectorAll<HTMLElement>('[data-scrollarea-track]');
      bars?.forEach((bar) => {
        bar.dataset.state = 'visible';
      });
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => {
        bars?.forEach((bar) => {
          bar.dataset.state = 'hidden';
        });
      }, scrollHideDelay);
    };

    return (
      <StyledViewport
        ref={composed}
        data-scrollarea-viewport=""
        onScroll={handleScroll}
        {...semanticProps}
        {...rest}
      />
    );
  },
);

ScrollAreaViewport.displayName = 'ScrollArea.Viewport';

const StyledScrollbar = styled.div<{ $orientation: Orientation }>`
  position: absolute;
  ${({ $orientation }) =>
    $orientation === 'vertical'
      ? `
    top: ${rem(4)};
    bottom: ${rem(4)};
    right: ${rem(4)};
    width: ${rem(10)};
  `
      : `
    left: ${rem(4)};
    right: ${rem(4)};
    bottom: ${rem(4)};
    height: ${rem(10)};
  `}
  border-radius: var(--ui-radius-full, ${rem(999)});
  background: var(--ui-scrollbar-track, rgba(0, 0, 0, 0.06));
  transition: opacity 0.2s ease;
  pointer-events: auto;
  z-index: 1;

  &[data-state='hidden'] {
    opacity: 0;
  }

  &[data-state='visible'] {
    opacity: 1;
  }
`;

const StyledThumbInner = styled.div<{ $orientation: Orientation }>`
  position: absolute;
  border-radius: inherit;
  background: var(--ui-scrollbar-thumb, rgba(0, 0, 0, 0.35));
  ${({ $orientation }) =>
    $orientation === 'vertical'
      ? `
    left: ${rem(2)};
    right: ${rem(2)};
    width: auto;
  `
      : `
    top: ${rem(2)};
    bottom: ${rem(2)};
    height: auto;
  `}
`;

function parseRemPx(r: `${number}rem`): number {
  return Number.parseFloat(r) * 10;
}

export interface ScrollAreaScrollbarProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation: Orientation;
  semanticId?: string;
}

const ScrollAreaScrollbar = React.forwardRef<HTMLDivElement, ScrollAreaScrollbarProps>(
  function ScrollAreaScrollbar({ orientation, semanticId, children, ...rest }, ref) {
    const trackRef = React.useRef<HTMLDivElement | null>(null);
    const localCtx = React.useMemo(() => ({ orientation, trackRef }), [orientation]);
    const semanticProps = getSemanticAttributes(semanticId);

    return (
      <ScrollbarLocalContext.Provider value={localCtx}>
        <StyledScrollbar
          ref={composeRefs(ref, trackRef)}
          data-orientation={orientation}
          data-scrollarea-track=""
          data-state="hidden"
          $orientation={orientation}
          {...semanticProps}
          {...rest}
        >
          {children}
        </StyledScrollbar>
      </ScrollbarLocalContext.Provider>
    );
  },
);

ScrollAreaScrollbar.displayName = 'ScrollArea.Scrollbar';

export interface ScrollAreaThumbProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const ScrollAreaThumb = React.forwardRef<HTMLDivElement, ScrollAreaThumbProps>(function ScrollAreaThumb(
  { semanticId, style: styleProp, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, ...rest },
  ref,
) {
  const { viewportRef, scrollHideDelay } = useScrollAreaContext();
  const local = React.useContext(ScrollbarLocalContext);
  if (!local) {
    throw new Error('ScrollArea.Thumb must be used within ScrollArea.Scrollbar');
  }
  const { orientation, trackRef: trackRefFromCtx } = local;
  const thumbRef = React.useRef<HTMLDivElement | null>(null);
  const composed = composeRefs(ref, thumbRef);
  const [thumbSize, setThumbSize] = React.useState(0);
  const [thumbPos, setThumbPos] = React.useState(0);
  const hideTimer = React.useRef<number>();
  const dragStart = React.useRef<{ pointerOffsetInThumb: number; scrollStart: number } | null>(null);
  const semanticProps = getSemanticAttributes(semanticId);

  const setBarsVisible = React.useCallback(() => {
    const root = thumbRef.current?.closest('[data-scrollarea-root]') as HTMLElement | null;
    const bars = root?.querySelectorAll<HTMLElement>('[data-scrollarea-track]');
    bars?.forEach((bar) => {
      bar.dataset.state = 'visible';
    });
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      bars?.forEach((bar) => {
        bar.dataset.state = 'hidden';
      });
    }, scrollHideDelay);
  }, [scrollHideDelay]);

  const updateMetrics = React.useCallback(() => {
    const vp = viewportRef.current;
    const track = trackRefFromCtx.current;
    if (!vp || !track) return;

    if (orientation === 'vertical') {
      const vpH = vp.clientHeight;
      const sh = vp.scrollHeight;
      const trackH = track.clientHeight;
      if (sh <= vpH) {
        setThumbSize(0);
        setThumbPos(0);
        return;
      }
      const ratio = vpH / sh;
      const size = Math.max(trackH * ratio, parseRemPx(rem(24)));
      setThumbSize(size);
      const maxScroll = sh - vpH;
      const maxThumb = trackH - size;
      const pos = maxScroll > 0 ? (vp.scrollTop / maxScroll) * maxThumb : 0;
      setThumbPos(pos);
    } else {
      const vpW = vp.clientWidth;
      const sw = vp.scrollWidth;
      const trackW = track.clientWidth;
      if (sw <= vpW) {
        setThumbSize(0);
        setThumbPos(0);
        return;
      }
      const ratio = vpW / sw;
      const size = Math.max(trackW * ratio, parseRemPx(rem(24)));
      setThumbSize(size);
      const maxScroll = sw - vpW;
      const maxThumb = trackW - size;
      const pos = maxScroll > 0 ? (vp.scrollLeft / maxScroll) * maxThumb : 0;
      setThumbPos(pos);
    }
  }, [orientation, viewportRef, trackRefFromCtx]);

  React.useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return undefined;
    updateMetrics();
    const ro = new ResizeObserver(updateMetrics);
    ro.observe(vp);
    if (trackRefFromCtx.current) ro.observe(trackRefFromCtx.current);
    vp.addEventListener('scroll', updateMetrics);
    return () => {
      ro.disconnect();
      vp.removeEventListener('scroll', updateMetrics);
    };
  }, [viewportRef, trackRefFromCtx, updateMetrics]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    onPointerDown?.(e);
    if (e.defaultPrevented) return;
    e.preventDefault();
    const vp = viewportRef.current;
    const thumbEl = thumbRef.current;
    if (!vp || !thumbEl) return;
    thumbEl.setPointerCapture(e.pointerId);
    if (orientation === 'vertical') {
      dragStart.current = {
        pointerOffsetInThumb: e.clientY - thumbEl.getBoundingClientRect().top,
        scrollStart: vp.scrollTop,
      };
    } else {
      dragStart.current = {
        pointerOffsetInThumb: e.clientX - thumbEl.getBoundingClientRect().left,
        scrollStart: vp.scrollLeft,
      };
    }
    setBarsVisible();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    onPointerMove?.(e);
    if (!dragStart.current) return;
    const vp = viewportRef.current;
    const track = trackRefFromCtx.current;
    if (!vp || !track) return;
    if (orientation === 'vertical') {
      const trackRect = track.getBoundingClientRect();
      const thumbH = thumbSize;
      const sh = vp.scrollHeight;
      const vpH = vp.clientHeight;
      const maxScroll = sh - vpH;
      const maxThumb = track.clientHeight - thumbH;
      const thumbTopInTrack = e.clientY - trackRect.top - dragStart.current.pointerOffsetInThumb;
      const nextScroll = maxThumb > 0 ? (thumbTopInTrack / maxThumb) * maxScroll : 0;
      vp.scrollTop = Math.min(Math.max(0, nextScroll), maxScroll);
    } else {
      const trackRect = track.getBoundingClientRect();
      const thumbW = thumbSize;
      const sw = vp.scrollWidth;
      const vpW = vp.clientWidth;
      const maxScroll = sw - vpW;
      const maxThumb = track.clientWidth - thumbW;
      const thumbLeftInTrack = e.clientX - trackRect.left - dragStart.current.pointerOffsetInThumb;
      const nextScroll = maxThumb > 0 ? (thumbLeftInTrack / maxThumb) * maxScroll : 0;
      vp.scrollLeft = Math.min(Math.max(0, nextScroll), maxScroll);
    }
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    onPointerUp?.(e);
    dragStart.current = null;
    try {
      thumbRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  };

  const handleCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    onPointerCancel?.(e);
    endDrag(e);
  };

  return (
    <StyledThumbInner
      ref={composed}
      $orientation={orientation}
      data-orientation={orientation}
      {...semanticProps}
      {...rest}
      style={{
        ...(orientation === 'vertical'
          ? { height: thumbSize, top: thumbPos }
          : { width: thumbSize, left: thumbPos }),
        ...styleProp,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={handleCancel}
    />
  );
});

ScrollAreaThumb.displayName = 'ScrollArea.Thumb';

export const ScrollArea = {
  Root: ScrollAreaRoot,
  Viewport: ScrollAreaViewport,
  Scrollbar: ScrollAreaScrollbar,
  Thumb: ScrollAreaThumb,
};
